import icCalendar from "@/assets/icons/ic-calendar.svg?raw";
import icMail from "@/assets/icons/ic-mail.svg?raw";
import { formatDate } from "@/lib/utils";
import { IAdminDetailRes, IAdminRole } from "@/services/admin-management-service";
import DOMPurify from "dompurify";

type AdminStatus = "active" | "inactive";

const statusConfig: Record<AdminStatus, { label: string; bg: string; text: string }> = {
  active: {
    label: "Active",
    bg: "bg-[#43946C]",
    text: "text-[#000000]",
  },
  inactive: {
    label: "Inactive",
    bg: "bg-[#EBEBEB]",
    text: "text-[#000000]",
  },
};

const roleConfig: Record<IAdminRole, { label: string }> = {
  administrator: { label: "Administrator" },
  editor: { label: "Editor" },
  viewer: { label: "Viewer" },
};

interface AdminProfileCardProps {
  detail: IAdminDetailRes;
  role: IAdminRole;
}

export const AdminProfileCard = ({ detail, role }: AdminProfileCardProps) => {
  const status = (detail.status as AdminStatus) || "active";
  const sc = statusConfig[status] ?? statusConfig.active;
  const rc = roleConfig[role] ?? roleConfig.viewer;

  return (
    <div className="bg-white rounded p-5 border border-[#00000017]">
      {/* Title section: name + badges */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-2">
          <span className="text-2xl font-bold text-[#000000] break-all whitespace-pre-line">
            {detail.name || "—"}
          </span>
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center gap-1 h-6 rounded-[4px] px-[15px]  text-sm ${sc.bg} ${sc.text}`}
            >
              {sc.label}
            </span>
            <span
              className={`min-w-[85px] inline-flex items-center justify-center h-6 rounded-[4px] px-[14.5px] text-sm text-black bg-[#FFAA72]`}
            >
              {rc.label}
            </span>
          </div>
        </div>

        {/* Divider */}
        <div className="h-[1px] bg-[#00000017] my-1" />

        {/* Info rows */}
        <div className="flex flex-col gap-2">
          {/* Email */}
          <div className="flex items-center gap-2">
            <span
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(icMail.replace('fill="black"', `fill="#B0B0B0"`)),
              }}
              className="w-[18px] h-[18px] inline-flex items-center"
            />

            <span className="text-sm text-[#B0B0B0] min-w-[68px]">Email</span>
            <span className="text-base text-[#000000] whitespace-pre-line break-all">
              {detail.email || "—"}
            </span>
          </div>

          {/* Last login */}
          {detail.last_login_at && (
            <div className="flex items-center gap-2">
              <span
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    icCalendar.replace('fill="#202020"', `fill="#B0B0B0"`),
                  ),
                }}
                className="w-[18px] h-[18px] inline-flex items-center"
              />

              <span className="text-sm text-[#B0B0B0] min-w-[68px]">최종 로그인</span>
              <span className="text-base text-[#000000]">
                {formatDate(detail.last_login_at, "YYYY-MM-DD HH:mm")}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
